/**
 * V2 category catalog — the full categories table (2,000+ rows), paged
 * and cached once, plus slug helpers. Categories are looked up by
 * slugified name in URLs (V1 convention: /adventure/category/{slug}).
 */
import * as gateway from './gateway.js';
import { cached } from './query.js';
import { slugify } from '../../assets/js/shared/slugify.js';
import { registerCategoryGraph } from '../ui/icons.js';

export async function fetchAllCategories() {
  return cached('categories:all', async () => {
    const all = [];
    for (let offset = 0; ; offset += 1000) {
      const { data, error } = await gateway.rawQuery(gateway.TABLES.CATEGORIES, {
        select: 'id,name,emoji,description,parent_id,is_set,can_tag',
        order: 'name.asc',
        limit: 1000,
        offset,
      });
      if (error) throw error;
      all.push(...(data || []));
      if (!data || data.length < 1000) break;
    }
    // Leaf categories inherit their branch's ink icon through this graph
    // ("Yosemite" → National Parks) — see src/ui/icons.js
    registerCategoryGraph(all);
    return all;
  }, { ttl: 10 * 60_000 });
}

export function categorySlug(category) {
  return slugify(category?.name || '');
}

export async function findCategoryBySlug(slug) {
  const all = await fetchAllCategories();
  return all.find((c) => categorySlug(c) === slug) || null;
}

/**
 * Ids visible under a catalog search: matches plus their ancestors (so
 * the branch renders) and descendants (so context stays) — V1's picker
 * behavior (category-picker.js buildCategoryStructure). null = no term.
 */
export function searchVisibleIds(cats, term) {
  if (!term) return null;
  const byId = new Map(cats.map((c) => [c.id, c]));
  const needle = term.toLowerCase();
  const visible = new Set();
  const addDescendants = (id) => {
    cats.forEach((c) => {
      if (c.parent_id === id && !visible.has(c.id)) {
        visible.add(c.id);
        addDescendants(c.id);
      }
    });
  };
  cats.forEach((cat) => {
    if (!cat.name.toLowerCase().includes(needle) &&
        !(cat.description || '').toLowerCase().includes(needle)) return;
    visible.add(cat.id);
    let current = cat;
    while (current?.parent_id) {
      visible.add(current.parent_id);
      current = byId.get(current.parent_id);
    }
    addDescendants(cat.id);
  });
  return visible;
}

// ---------------------------------------------------------------------------
// "Parks & Protected Areas" visibility (V1 state.js:198-264 — same
// localStorage key, so the preference carries between V1 and V2)
// ---------------------------------------------------------------------------
const SHOW_STATE_PARKS_KEY = 'yoink_show_state_parks';

export function getShowStateParks() {
  try {
    return localStorage.getItem(SHOW_STATE_PARKS_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setShowStateParks(show) {
  try {
    localStorage.setItem(SHOW_STATE_PARKS_KEY, show ? 'true' : 'false');
  } catch {
    // private mode — session-only
  }
}

/**
 * The "Parks & Protected Areas" category and its whole subtree. Cached
 * per catalog array: the ranked search below asks on every keystroke.
 */
const parksSubtreeCache = new WeakMap();
export function parksSubtreeIds(allCats) {
  if (parksSubtreeCache.has(allCats)) return parksSubtreeCache.get(allCats);
  const root = allCats.find((c) => (c.name || '').toLowerCase().trim() === 'parks & protected areas');
  const ids = new Set(root ? [root.id] : []);
  let grew = Boolean(root);
  while (grew) {
    grew = false;
    for (const c of allCats) {
      if (c.parent_id && ids.has(c.parent_id) && !ids.has(c.id)) {
        ids.add(c.id);
        grew = true;
      }
    }
  }
  parksSubtreeCache.set(allCats, ids);
  return ids;
}

/** Category-listing filter: hide the parks subtree unless enabled. */
export function filterStateParks(cats, allCats, show) {
  if (show) return cats;
  const hidden = parksSubtreeIds(allCats);
  return cats.filter((c) => !hidden.has(c.id));
}

// ---------------------------------------------------------------------------
// Ranked category search — the log form's picker and Discover's search
// (2026-09-15). Before this, both were plain substring matches in catalog
// (A–Z) order, so "park" returned twelve state parks starting at "A" and
// the "Park" category itself never made the cut. The parks subtree runs
// to thousands of rows; it is the noise floor of every "park", "state",
// "beach" and "lake" search.
//
// The rule is RANK AND FOLD, never hide: hiding the subtree behind the
// browse-page toggle was tried on this picker and reverted 2026-08-07
// (typing "Death Valley" got "No category by that name" with no toggle
// in sight to explain why). Nothing is dropped here — a park typed by
// name still surfaces; a park *flood* folds behind one counted row.
// ---------------------------------------------------------------------------

/** Park rows beyond this fold into one "N parks also match" row. */
export const PARKS_FOLD_MIN = 3;

const TIER_EXACT = 0;
const TIER_PREFIX = 1;
const TIER_WORD = 2;
const TIER_CONTAINS = 3;
const TIER_DESCRIPTION = 4;

function matchTier(cat, q, withDescription) {
  const name = (cat.name || '').toLowerCase();
  if (name === q) return TIER_EXACT;
  if (name.startsWith(q)) return TIER_PREFIX;
  const at = name.indexOf(q);
  if (at > 0) return /[\s&/(-]/.test(name[at - 1]) ? TIER_WORD : TIER_CONTAINS;
  if (withDescription && (cat.description || '').toLowerCase().includes(q)) return TIER_DESCRIPTION;
  return -1;
}

/**
 * Catalog matches for a typed term, split into the categories outside the
 * parks subtree (`others`) and inside it (`parks`), each ranked: exact
 * name, then name-prefix, then word-prefix ("Dog Park" for "park"), then
 * contains, then description (opt-in — Discover uses it, the picker does
 * not), and A–Z within a tier. `allCats` is the FULL catalog (the subtree
 * walk needs the untaggable root); `cats` is whatever subset the caller
 * lets through (the picker drops Sets and can_tag=false first).
 */
export function rankCategoryMatches(cats, term, allCats = cats, { description = false } = {}) {
  const q = (term || '').trim().toLowerCase();
  if (!q) return { others: [], parks: [] };
  const inParks = parksSubtreeIds(allCats);
  const others = [];
  const parks = [];
  for (const c of cats) {
    const tier = matchTier(c, q, description);
    if (tier < 0) continue;
    (inParks.has(c.id) ? parks : others).push({ c, tier });
  }
  const order = (a, b) => a.tier - b.tier || (a.c.name || '').localeCompare(b.c.name || '');
  return {
    others: others.sort(order).map((m) => m.c),
    parks: parks.sort(order).map((m) => m.c),
  };
}

/**
 * The rows a result list shows, with the park flood folded. `rows` is at
 * most `limit` long; `folded` is how many park matches sit behind the
 * fold row (0 = none, render no fold). A handful of parks (≤ PARKS_FOLD_MIN)
 * stays inline after the other matches — so "yellowstone" behaves exactly
 * as before — and so does everything when the person has switched the
 * parks toggle on (`showParks`) or tapped the fold open (`expanded`).
 */
export function foldParkMatches({ others, parks }, { limit, showParks = false, expanded = false } = {}) {
  const inline = showParks || expanded || parks.length <= PARKS_FOLD_MIN;
  if (inline) {
    const all = [...others, ...parks];
    return { rows: limit ? all.slice(0, limit) : all, folded: 0, more: limit ? Math.max(0, all.length - limit) : 0 };
  }
  // The fold row takes one slot of the limit
  const keep = limit ? Math.max(0, limit - 1) : others.length;
  return { rows: others.slice(0, keep), folded: parks.length, more: 0 };
}
